import { Injectable, inject } from '@angular/core';
import {
  CommentsApi,
  GetCommentPageInfoSortEnum,
  GetMentionTargetDetailTypeEnum,
  ListCommentsSortEnum,
  ListMentionSuggestionsTypeEnum,
  type Comment,
  type CommentEditHistory,
  type CommentList,
  type CommentPageInfo,
  type CommentUnreadCounts,
  type MentionSuggestion,
  type MentionTargetDetail,
  type ReadCursor,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { FrontendUserPreferencesApi } from '@tourfold/libraries/tf-api-client-frontend-v2';

/**
 * Thin wrapper over the generated `CommentsApi`. Stays promise-first to match the rest of the
 * platform/* services (e.g. `DocumentsService`).
 *
 * F1 only exposes the read-only list. Compose / edit / delete / restore / read-tracking are
 * added in F2–F4 as the corresponding UI lands.
 */
/** Sort presets accepted by `<tf-comments>`. Mirrors the backend's allow-list. */
export const COMMENTS_SORT_VALUES = [
  'threaded:desc',
  'threaded:asc',
  'flat:desc',
  'flat:asc',
] as const;
export type CommentsSort = (typeof COMMENTS_SORT_VALUES)[number];

@Injectable({ providedIn: 'root' })
export class CommentsService {
  private readonly api = inject(CommentsApi);
  private readonly preferencesApi = inject(FrontendUserPreferencesApi);

  /** Default page size for the comments list. */
  static readonly DEFAULT_PAGE_SIZE = 25;

  /** Default sort when the user hasn't picked one yet. */
  static readonly DEFAULT_SORT: CommentsSort = 'threaded:desc';

  /**
   * Fetches a page of comments for `(targetType, targetId)`.
   *
   * In **threaded** mode the response items array interleaves each root with its replies
   * (`[root, ...replies, root, ...replies]`). In **flat** mode the array is a single sorted
   * stream. Pagination on threaded mode counts root comments — the items array also contains
   * the replies of each root on the page.
   */
  list(args: {
    targetType: string;
    targetId: string;
    page?: number;
    size?: number;
    sort?: ListCommentsSortEnum;
  }): Promise<CommentList> {
    return this.api.listComments({
      targetType: args.targetType,
      targetId: args.targetId,
      page: args.page,
      size: args.size ?? CommentsService.DEFAULT_PAGE_SIZE,
      sort: args.sort,
    });
  }

  /**
   * Fetches a single comment with resolved author + caller-scoped `is_unread` state. Used by the
   * SSE-driven thin refetch on `COMMENT_CREATED:V2` (the event payload carries `author_id` only,
   * not the resolved display name) and by deep-link openers that may target a row outside the
   * currently-loaded page window.
   */
  getById(id: string): Promise<Comment> {
    return this.api.getComment({ id });
  }

  /**
   * Resolves which page of the list endpoint contains [id] under the given sort + size. Used by
   * the deep-link path so the component can load pages 0..page in one go and scroll the row
   * into view, instead of paginating by hand. The `size` defaults to the same `DEFAULT_PAGE_SIZE`
   * we use for `list()` so the index lookup stays aligned with what the user actually sees.
   */
  getPageInfo(args: {
    id: string;
    sort: ListCommentsSortEnum;
    size?: number;
  }): Promise<CommentPageInfo> {
    return this.api.getCommentPageInfo({
      id: args.id,
      // Sort enum strings are identical between list / page-info; the generator just produces
      // a separate type per operation. Cast keeps the public surface single-enum.
      sort: args.sort as unknown as GetCommentPageInfoSortEnum,
      size: args.size ?? CommentsService.DEFAULT_PAGE_SIZE,
    });
  }

  /**
   * Posts a new comment. The server dedups on `(target, author, body, ~60s window)` and may
   * return an existing comment on a duplicate post — both code paths resolve with a `Comment`
   * (HTTP 201 for a true insert, 200 for the dedup hit). The caller doesn't need to distinguish:
   * the returned shape is the same and the `id` may already be visible in a previous list page.
   */
  create(args: {
    targetType: string;
    targetId: string;
    bodyMarkdown: string;
    parentCommentId?: string;
    /**
     * Filestore file ids to link as attachments. Inline references in the body via
     * `![alt](attachment:<uuid>)` must also appear here — the server enforces consistency.
     * Defaults to an empty array (no attachments).
     */
    attachmentIds?: readonly string[];
  }): Promise<Comment> {
    return this.api.createComment({
      createComment: {
        targetType: args.targetType,
        targetId: args.targetId,
        bodyMarkdown: args.bodyMarkdown,
        parentCommentId: args.parentCommentId,
        attachmentIds: args.attachmentIds ? [...args.attachmentIds] : [],
      },
    });
  }

  /**
   * Edits an existing comment's body. `editReason` is required (non-blank) when the caller is a
   * moderator editing another user's comment; ignored for author edits. The server enforces both
   * rules and returns 403 / 422 as appropriate.
   */
  update(args: {
    id: string;
    bodyMarkdown: string;
    editReason?: string;
    /**
     * Replacement attachment list. **Omit (or pass `undefined`) to leave the existing set
     * untouched.** Pass an empty array to clear; pass a populated array to replace. Inline
     * references in `bodyMarkdown` must appear in the array — the server returns
     * `comments/attachment-inline-not-linked` (422) otherwise.
     */
    attachmentIds?: readonly string[];
  }): Promise<Comment> {
    return this.api.updateComment({
      id: args.id,
      updateComment: {
        bodyMarkdown: args.bodyMarkdown,
        editReason: args.editReason,
        // Only include `attachment_ids` when the caller explicitly opted in; otherwise the
        // backend's "key absent = preserve" PATCH convention applies. Spreading conditionally
        // ensures `undefined` doesn't get serialised as `null` (which would clear attachments).
        ...(args.attachmentIds !== undefined && { attachmentIds: [...args.attachmentIds] }),
      },
    });
  }

  /**
   * Soft-deletes a comment. Idempotent — repeated calls succeed and leave the row in the
   * tombstoned state. Author can delete own; moderator can delete any.
   */
  delete(id: string): Promise<void> {
    return this.api.deleteComment({ id });
  }

  /**
   * Restores a soft-deleted (tombstoned) comment. Moderator-only. Returns the freshly-restored
   * `Comment` so callers can patch their in-memory list with the canonical state.
   */
  restore(id: string): Promise<Comment> {
    return this.api.restoreComment({ id });
  }

  /**
   * Loads the audit trail of edits applied to a comment, in chronological order. Each entry
   * carries the previous body, the editor reference, the moderator-edit flag, and the optional
   * reason. Empty history when the comment has never been edited.
   */
  getEditHistory(id: string): Promise<CommentEditHistory> {
    return this.api.getCommentEditHistory({ id });
  }

  /**
   * Advances the caller's read cursor on `(targetType, targetId)` up to and including
   * `commentId`. Used by the auto-mark-read-on-scroll path: when the newest visible row hasn't
   * changed for ~1.5 s, the component calls this with that row's id so the cursor catches up.
   *
   * The endpoint is idempotent — calling it with a cursor that already covers `commentId`
   * succeeds without side-effects. Returns the post-advance cursor.
   */
  markReadUntil(args: {
    targetType: string;
    targetId: string;
    commentId: string;
  }): Promise<ReadCursor> {
    return this.api.markCommentsReadUntil({
      markReadUntil: {
        targetType: args.targetType,
        targetId: args.targetId,
        commentId: args.commentId,
      },
    });
  }

  /** Per-row mark-read action — flips the comment to read for the calling user. */
  markRead(id: string): Promise<void> {
    return this.api.markCommentRead({ id });
  }

  /** Per-row mark-unread action — flips the comment to unread for the calling user. */
  markUnread(id: string): Promise<void> {
    return this.api.markCommentUnread({ id });
  }

  /**
   * Picker-side search for mention targets. Wires the backend's prefix-then-contains ranking
   * through to the suggestion popup. Returns an empty list when nothing matches — no special
   * handling needed in the caller for "no matches". Requires the `comments.create` grant; the
   * backend gates this so read-only viewers never see the picker.
   *
   * Debouncing belongs in the consumer (the Tiptap suggestion adapter does it natively); this
   * wrapper stays cheap and one-shot.
   */
  findMentionTargets(args: {
    q?: string;
    type?: string;
    limit?: number;
  }): Promise<MentionSuggestion[]> {
    return this.api
      .listMentionSuggestions({
        q: args.q,
        // The generated client narrows `type` to `'user'` (and future variants). The service
        // accepts `string` so callers can pass any future discriminator the backend supports
        // without a frontend release — the cast lets us treat the wire as schema-loose.
        type: args.type as ListMentionSuggestionsTypeEnum | undefined,
        limit: args.limit,
      })
      .then((response) => response.items);
  }

  /**
   * Lazy detail fetch for the read-path mention popover. Returns the live target's name, email
   * and roles when the chip is clicked; deleted targets come back with `isDeleted=true` and may
   * have empty `email`/`roles`. 404 is mapped to a rejected promise by the generated client —
   * the chip's open handler treats that as "deleted/unknown" and falls back to the cached label
   * the mention chip already shows.
   */
  getMentionDetail(args: { type: string; id: string }): Promise<MentionTargetDetail> {
    return this.api.getMentionTargetDetail({
      // Same enum-narrowing cast as `findMentionTargets`.
      type: args.type as GetMentionTargetDetailTypeEnum,
      id: args.id,
    });
  }

  /**
   * Adds an emoji reaction by the calling user to a comment. Idempotent at the server: a duplicate
   * add (same `(comment, user, emoji)` triple) returns the same comment shape with no extra
   * insertion. The promise resolves with the updated comment so the caller can replace the row in
   * its in-memory list — `reactions` will reflect the post-add aggregate.
   *
   * The frontend allow-list is intentionally a static const in
   * `reaction-picker.component.ts` mirroring the server default (`comments.reactions.allowed`).
   * Soft-deleted comments reject with 422 `comments.reaction-on-deleted-comment` — callers should
   * already hide the picker on tombstones; this just documents the error contract.
   */
  addReaction(args: { id: string; emoji: string }): Promise<Comment> {
    return this.api.addCommentReaction({
      id: args.id,
      addReactionRequest: { emoji: args.emoji },
    });
  }

  /**
   * Removes the calling user's reaction from a comment by its opaque reaction id. The id is read
   * from `reactions[*].your_reaction_id` on the aggregate (non-null iff `you_reacted == true`).
   * Returns 404 `comments/reaction-not-found` when the id does not exist or belongs to another
   * user — both cases surface identically so the API does not leak the existence of other users'
   * reaction rows. The allow-list is NOT enforced on the remove path so a user can always
   * un-react after the operator has tightened the allow-list.
   */
  removeReaction(args: { id: string; reactionId: string }): Promise<Comment> {
    return this.api.removeCommentReaction({
      id: args.id,
      reactionId: args.reactionId,
    });
  }

  /**
   * Batch unread count lookup for the custom-object list page. Returns a map keyed by target
   * id; targets not present in the response are treated as `0` by the caller (the server omits
   * zero-counts to keep the payload small).
   */
  async batchUnreadCounts(args: {
    targetType: string;
    targetIds: readonly string[];
  }): Promise<Map<string, number>> {
    if (args.targetIds.length === 0) return new Map();
    const response: CommentUnreadCounts = await this.api.batchUnreadCountComments({
      targetType: args.targetType,
      targetIds: args.targetIds.join(','),
    });
    const out = new Map<string, number>();
    for (const entry of response.counts ?? []) {
      out.set(entry.targetId, entry.unreadCount);
    }
    return out;
  }

  /**
   * Loads the user's persisted comments sort. Returns `null` when the user has never set one;
   * the caller falls back to {@link DEFAULT_SORT}. Failures resolve to `null` rather than
   * rejecting — sort prefs are non-critical and shouldn't block the comments list from
   * rendering.
   */
  async loadSortPreference(): Promise<CommentsSort | null> {
    try {
      const prefs = await this.preferencesApi.getCurrentUserPreferences();
      const sort = prefs.comments?.sort;
      return isCommentsSort(sort) ? sort : null;
    } catch {
      return null;
    }
  }

  /**
   * Persists the chosen sort to the user's preferences. Best-effort — a failed PATCH keeps the
   * locally-applied sort and just won't survive across reloads. Resolves with `true` on success.
   */
  async saveSortPreference(sort: CommentsSort): Promise<boolean> {
    try {
      await this.preferencesApi.patchCurrentUserPreferences({
        // The generated DTO is partial — anything we don't include stays unchanged. The
        // controller's `parsePreferencePatch` only writes paths it sees in the body.
        patchFrontendUserPreferences: {
          comments: { sort },
        },
      });
      return true;
    } catch {
      return false;
    }
  }
}

function isCommentsSort(value: unknown): value is CommentsSort {
  return typeof value === 'string' && (COMMENTS_SORT_VALUES as readonly string[]).includes(value);
}
